import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTour } from '../../context/TourContext';
import { getTopic, featureHasTour } from '../../help/registry';
import Icon from '../Icon';
import './HelpIcon.css';

/**
 * NER-1276 — Capa 1: inline contextual help.
 *
 * Renders a small `?` button next to a field. Clicking it opens an anchored
 * popover with the topic's title + description (i18n), and — when the topic's
 * feature has a tour — a "Start tour" action.
 *
 * Usage: <HelpIcon topicKey="modifiers.minMaxSelect" />
 *
 * The `data-help-target` attribute ties this instance to a registry key so the
 * CI coverage script (scripts/check-help-coverage.js) can verify every inline
 * help icon references a registered topic.
 */
function HelpIcon({ topicKey, placement = 'bottom', className = '' }) {
  const { t } = useTranslation();
  const { startTour } = useTour();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const topic = getTopic(topicKey);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  if (!topic) {
    // Misconfigured topicKey — fail loud in dev, render nothing in prod.
    // eslint-disable-next-line no-console
    console.warn(`[HelpIcon] unknown topicKey "${topicKey}" — add it to src/help/registry.js`);
    return null;
  }

  const showTour = featureHasTour(topic.feature);

  const handleStartTour = () => {
    setOpen(false);
    startTour(topic.feature);
  };

  return (
    <span ref={wrapperRef} className={`help-icon-wrapper ${className}`} data-help-target={topicKey}>
      <button
        type="button"
        className={`help-icon-btn ${open ? 'active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label={t(topic.titleKey)}
        aria-expanded={open}
        title={t(topic.titleKey)}
      >
        <Icon name="help_outline" size="small" />
      </button>

      {open && (
        <div className={`help-popover help-popover--${placement}`} role="tooltip">
          <h4 className="help-popover-title">{t(topic.titleKey)}</h4>
          <p className="help-popover-description">{t(topic.descriptionKey)}</p>
          {showTour && (
            <button type="button" className="help-popover-tour-link" onClick={handleStartTour}>
              <Icon name="play_circle_outline" size="small" />
              {t('help.startTour')}
            </button>
          )}
        </div>
      )}
    </span>
  );
}

export default HelpIcon;
